import { THREAD_NODE_KIND } from './api';
import type { DisplayCitation, DisplayDoc, PublicDocId, ThreadNode } from './api';

/** Adds recursive children to one tagged wire node without changing its payload variant. */
export type DocThreadTreeNode = ThreadNode & { children: DocThreadTreeNode[] };

/** Bundles the mixed rendered tree with its disclosed-only page projections. */
export interface DocThreadView {
  /** Contains both variants in backend response order after citation-based linking. */
  docTree: DocThreadTreeNode[];
  /** Contains disclosed documents only, preserving backend response order. */
  disclosedDocs: DisplayDoc[];
  /** Identifies the final disclosed document in depth-first render order. */
  bottomDisclosedDocId: PublicDocId | null;
}

/** Returns the public identity shared by both tagged variants. */
export function getThreadNodePublicId(node: ThreadNode): PublicDocId {
  return node.public_doc_id;
}

/** Returns a disclosed payload or null for an inaccessible structural node. */
function getThreadNodeDocument(node: ThreadNode): DisplayDoc | null {
  return node.kind === THREAD_NODE_KIND.Document ? node.doc : null;
}

/** Selects the legacy citation that defines disclosed-document parentage. */
function getDocumentParentCitation(citations: DisplayCitation[]): DisplayCitation | undefined {
  return (
    citations.find((citation) => citation.rel === 'reply_to' || citation.rel === 'ReplyTo') ??
    citations.find((citation) => citation.rel === 'Unspecified' || citation.rel === 'unspecified')
  );
}

/** Returns the variant-specific rendered parent identity, when present. */
function getThreadNodeParentPublicId(node: ThreadNode): PublicDocId | null {
  return node.kind === THREAD_NODE_KIND.Document
    ? (getDocumentParentCitation(node.doc.citations)?.doc ?? null)
    : node.parent_public_doc_id;
}

/** Links chronological tagged nodes while preserving response order for roots and children. */
function linkDocTree(nodes: ThreadNode[]): DocThreadTreeNode[] {
  const nodeMap = new Map<PublicDocId, DocThreadTreeNode>();
  const rootNodes: DocThreadTreeNode[] = [];

  nodes.forEach((node) => {
    nodeMap.set(getThreadNodePublicId(node), { ...node, children: [] });
  });

  nodes.forEach((node) => {
    const publicDocId = getThreadNodePublicId(node);
    const treeNode = nodeMap.get(publicDocId);
    const parentPublicDocId = getThreadNodeParentPublicId(node);
    const parent = parentPublicDocId ? nodeMap.get(parentPublicDocId) : undefined;

    if (!treeNode) return;
    if (parent) {
      parent.children.push(treeNode);
    } else {
      rootNodes.push(treeNode);
    }
  });

  return rootNodes;
}

/** Finds the final disclosed node in the exact depth-first render order. */
function findBottomDisclosedDocId(rootNodes: DocThreadTreeNode[]): PublicDocId | null {
  let bottomDisclosedDocId: PublicDocId | null = null;

  const visit = (node: DocThreadTreeNode): void => {
    if (node.kind === THREAD_NODE_KIND.Document) {
      bottomDisclosedDocId = node.public_doc_id;
    }
    node.children.forEach(visit);
  };
  rootNodes.forEach(visit);

  return bottomDisclosedDocId;
}

/** Builds the mixed structural tree and disclosed-only page projections. */
export function buildDocThreadView(nodes: ThreadNode[]): DocThreadView {
  const docTree = linkDocTree(nodes);
  const disclosedDocs = nodes.flatMap((node) => {
    const document = getThreadNodeDocument(node);
    return document ? [document] : [];
  });
  const bottomDisclosedDocId = findBottomDisclosedDocId(docTree);

  return { docTree, disclosedDocs, bottomDisclosedDocId };
}
